import * as React from "react";
import ReactTooltip from 'react-tooltip';

import "./confirm-tab.scss";

import {cst, cstAssign}          from "@Common/react/chain-services";
import {consumeContext}          from "@Common/react/context";
import {scopedState}             from "@Common/react/scoped-state";
import {UseState}                from "@Common/react/use-state";
import {Responsive}              from "@Common/ui-components/responsive";
import {addURLParameterToWindow} from "@Common/utils/add-url-parameter";
import {Types}                   from "@Common/types";
import {ModalLayout}             from "@Xpresso/routes/common/modal-layout/modal-layout";
import {FIELD_NAMES}             from "../../../common/general-form-state";
import {OfficeCreateTabs}        from "../../office-create-tabs";
import {getOfficeFields}         from "../general-tab/general-tab";

export const ConfirmTab = ({step, modal, office, reload, idCompanyBranded=null} : {
    step: Types.Common.React.UseState.UseStateNext<string>,
    modal: Types.Common.UiComponents.ModalService3.ModalService3Next<null>,
    office: Types.Xpresso.Account.Admin.OfficeManagement.CreateModal.OfficeState,
    reload: () => any,
    idCompanyBranded ?: string,
}) => cst<{
    apis       : Types.Apis.Loaders.XpressoLoadApis,
    responsive : any,
    uploading  : Types.Common.React.UseState.UseStateNext<boolean>,
    office     : Types.Xpresso.Account.Admin.OfficeManagement.CreateModal.OfficeInputs,
    routerHistory
}>(
    consumeContext("apis"),
    consumeContext("routerHistory"),
    cstAssign("uploading", (_, next) => UseState({next})),
    cstAssign("office", (_,next) => next(office.value.inputs)),
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({apis, uploading, routerHistory, responsive},next) => (
        ModalLayout({
            title: "Create New Office",
            content: (
                <>
                    {responsive.ge("md") && OfficeCreateTabs({step})}
                    {next()}
                </>
            ),
            buttons: [
                {
                    label: "Back",
                    onClick: () => {
                        addURLParameterToWindow({parameter: "step", value: "office-address"});
                        step.onChange("office-address");
                    },
                },
                {
                    label: "Create Office",
                    primary: true,
                    disabled: uploading.value,
                    onClick: async () => {
                        uploading.onChange(true);
                        const createFields = mapAddress(office.value.inputs);
                        if(idCompanyBranded) {
                            createFields.idCompanyBranded = idCompanyBranded
                        }
                        const result = await apis.next.branch.createBranch({createFields});
                        if (!result?.statusCode || result?.statusCode !== "success") {
                            if(result?.data) {
                                const errors = JSON.parse(result.data);
                                errors.map((error) => {
                                    scopedState(office, ['errors', error.mapping]).onChange(error.message)
                                });
                                if(errors.some((error:{mapping:typeof FIELD_NAMES[0]}) => getOfficeFields().map(a => a.mapping).includes(error.mapping))) {
                                    step.onChange("general");
                                } else {
                                    step.onChange("login");
                                }
                            } else {
                                alert(result.message);
                            }
                        } else {
                            step.onChange("general");
                            office.onChange({
                                inputs: {},
                                errors: {},
                            });
                            reload();
                            window.opener?.postMessage?.({
                                type: "branchCreated",
                                payload: {...createFields, idBranch: result.data.idBranch},
                            });
                            routerHistory.replace('/account/admin/office?search=' + result.data.idBranch);
                        }
                        uploading.onChange(false);
                    },
                },
            ]
        })
    ),
    ({office}) => (
        <div className="confirm-tab-f9y">
            <ReactTooltip  id="confirm-tab" place="bottom" delayShow={100}/>
            <div className="section">
                <div className="header">
                    <div className="title">
                        General Information
                    </div>
                    <a className="edit" xd-qa-type="link" xd-qa-name="Edit General Information" onClick={() => step.onChange("general")}>
                        Edit
                    </a>
                </div>
                <div className="card">
                    <div title={office.general.branchName} className="title title-office" xd-qa-type="label" xd-qa-name="Branch Name">
                        {office.general.branchName}
                    </div>
                    <div className="value" xd-qa-type="label" xd-qa-name="Branch Manager">
                        {office.general.branchManagerName}
                    </div>
                    <div className="value" xd-qa-type="label" xd-qa-name="Branch Phone 1" title={office.general.branchPhone1}>
                        {office.general.branchPhone1}
                    </div>
                    <div className="value" xd-qa-type="label" xd-qa-name="Branch Phone 2" title={office.general.branchPhone2}>
                        {office.general.branchPhone2}
                    </div>
                    <div className="value" xd-qa-type="label" xd-qa-name="Branch Email">
                        {office.general.branchEmail}
                    </div>
                    {(office.general.branchAdmin1Name || office.general.branchAdmin1Email || office.general.branchAdmin2Name || office.general.branchAdmin2Email) && <div className="vertical-split">
                        <div className="item">
                            <div className="label">
                                Admin 1
                            </div>
                            <div className="value" xd-qa-type="label" xd-qa-name="Branch Admin 1 Name" title={office.general.branchAdmin1Name}>
                                {office.general.branchAdmin1Name}
                            </div>
                            <div className="value" xd-qa-type="label" xd-qa-name="Branch Admin 1 Email" title={office.general.branchAdmin1Email}>
                                {office.general.branchAdmin1Email}
                            </div>
                        </div>
                        {(office.general.branchAdmin2Name || office.general.branchAdmin2Email) && <div className="item">
                            <div className="label">
                                Admin 2
                            </div>
                            <div className="value" xd-qa-type="label" xd-qa-name="Branch Admin 2 Name" title={office.general.branchAdmin2Name}>
                                {office.general.branchAdmin2Name}
                            </div>
                            <div className="value" xd-qa-type="label" xd-qa-name="Branch Admin 2 Email" title={office.general.branchAdmin2Email}>
                                {office.general.branchAdmin2Email}
                            </div>
                        </div>}
                    </div>}
                    {office.general.branchLinkId && <div className="item">
                        <div className="label">
                            Link ID
                        </div>
                        <div className="value" xd-qa-type="label" xd-qa-name="Link ID" title={office.general.branchLinkId}>
                            {office.general.branchLinkId}
                        </div>
                    </div>}
                </div>
            </div>
            {
                !idCompanyBranded &&
                    <div className="section">
                        <div className="header">
                            <div className="title">
                                Login Information
                            </div>
                            <a className="edit" xd-qa-type="link" xd-qa-name="Edit Login Information" onClick={() => step.onChange("login")}>
                                Edit
                            </a>
                        </div>
                        <div className="card">
                            <div className="title" xd-qa-type="label" xd-qa-name="Username">
                                {office.userName}
                            </div>
                        </div>
                    </div>
            }
            <div className="section">
                <div className="header">
                    <div className="title">
                        Office Address
                    </div>
                    <a className="edit" xd-qa-type="link" xd-qa-name="Edit Office Address" onClick={() => step.onChange("office-address")}>
                        Edit
                    </a>
                </div>
                <div className="card">
                    <div className="title" xd-qa-type="label" xd-qa-name="Shipping Address Company" title={office.address.company}>
                        {office.address.company}
                    </div>
                    {office.address.contactName && <div className="subtitle" xd-qa-type="label" xd-qa-name="Shipping Address Contact" title={office.address.contactName}>
                        {office.address.contactName}
                    </div>}
                    <div className="vertical-split">
                        <div className="item">
                            <div className="label" xd-qa-type="label" xd-qa-name="Address">Address</div>
                            <div className="value" title={office.address.address1} xd-qa-type="text" xd-qa-name="Address 1">{office.address.address1}</div>
                            {office.address.address2 && <div className="value" title={office.address.address2} xd-qa-type="text" xd-qa-name="Address 2">{office.address.address2}</div>}
                            {(office.address.city || office.address.state || office.address.postalCode) && (
                                <div className="value" title={[
                                    office.address.city,
                                    office.address.state,
                                    office.address.postalCode
                                ].filter(Boolean).join(", ")}
                                xd-qa-type="text"
                                xd-qa-name="City State Zip">
                                    {[
                                        office.address.city,
                                        office.address.state,
                                        office.address.postalCode
                                    ].filter(Boolean).join(", ")}
                                </div>
                            )}
                        </div>
                        {office.address.contactPhone && (<div className="item">
                            <div className="label" xd-qa-type="label" xd-qa-name="Phone">Phone</div>
                            <div className="value" xd-qa-type="text" xd-qa-name="Phone Contact">{office.address.contactPhone}</div>
                        </div>)}
                    </div>
            </div>
            </div>
        </div>
    )
);

const mapAddress = (inputs) => {
    const createFields = {
        ...inputs,
        ...inputs.general,
        branchAddressType: inputs.address.type,
        branchAddressCompany: inputs.address.company,
        branchAddressContactName: inputs.address.contactName,
        branchAddress1: inputs.address.address1,
        branchAddress2: inputs.address?.address2,
        branchCity: inputs.address.city,
        branchState: inputs.address.state,
        branchZip: inputs.address.postalCode,
        branchCountry: inputs.address.countryCode,
        branchAddressContactPhone: inputs.address.contactPhone,
    }
    delete createFields.general;
    delete createFields.address;
    delete createFields.matchPassword
    return createFields;
}
